import React from 'react';

function PrivacyPolicy() {
  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#111827',
      color: '#e5e7eb',
      padding: '40px 20px',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif",
      lineHeight: 1.7,
    },
    content: {
      maxWidth: '800px',
      margin: '0 auto',
    },
    h1: {
      color: '#ffffff',
      fontSize: '2rem',
      marginBottom: '0.5rem',
    },
    lastUpdated: {
      color: '#9ca3af',
      fontSize: '0.9rem',
      marginBottom: '2rem',
    },
    h2: {
      color: '#ffffff',
      fontSize: '1.25rem',
      marginTop: '2rem',
      marginBottom: '1rem',
    },
    p: {
      color: '#d1d5db',
      marginBottom: '1rem',
    },
    ul: {
      marginLeft: '1.5rem',
      marginBottom: '1rem',
      color: '#d1d5db',
    },
    li: {
      marginBottom: '0.5rem',
    },
    highlight: {
      backgroundColor: '#1f2937',
      borderRadius: '8px',
      padding: '1rem 1.25rem',
      margin: '1rem 0',
    },
    link: {
      color: '#2563eb',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <h1 style={styles.h1}>Privacy Policy</h1>
        <p style={styles.lastUpdated}>Last updated: April 2026</p>

        <p style={styles.p}>
          Cue Hub ("we", "our", or "the app") is a pool tournament application that offers 
          two main features: tournament notifications to alert players when matches are ready, and 
          a manual shot clock and scoreboard for managing individual matches. This privacy policy 
          explains how we collect, use, and protect your information.
        </p>

        <h2 style={styles.h2}>Information We Collect</h2>
        <p style={styles.p}>
          The information we collect depends on which features you use:
        </p>
        
        <p style={styles.p}><strong>For Tournament Notifications:</strong></p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            <strong>Mobile phone number</strong> — Used to identify you as a registered player in the tournament system
          </li>
          <li style={styles.li}>
            <strong>Device push token</strong> — A unique identifier generated by your device that allows us to send you push notifications
          </li>
          <li style={styles.li}>
            <strong>Device information</strong> — Basic device details (device name, platform) to help identify your registered devices
          </li>
        </ul>

        <p style={styles.p}><strong>For Player Registration:</strong></p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            <strong>Full name</strong> — Used to identify you within the tournament system
          </li>
          <li style={styles.li}>
            <strong>Email address</strong> — Used to communicate tournament-related information with you
          </li>
          <li style={styles.li}>
            <strong>Mobile phone number</strong> — Used to link your registration to your player profile and enable notifications
          </li>
          <li style={styles.li}>
            <strong>CueScore profile data</strong> — If you link your CueScore account, we store your CueScore player ID and profile image URL to display your profile within the app
          </li>
        </ul>

        <p style={styles.p}><strong>For Manual Shot Clock / Scoreboard (Mobile App):</strong></p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            <strong>No data collected</strong> — The manual shot clock and scoreboard features in the mobile app run entirely on your device.
            Player names, scores, and match data are stored locally on your device only and are not transmitted to our servers.
          </li>
        </ul>

        <p style={styles.p}><strong>For Shot Clock / Scoreboard (Web App / Tablet):</strong></p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            When using the web-based tablet scoreboard, match data including player names and scores is synced to our servers in real time.
            This is necessary to keep scores accurate across devices, allow organisers to monitor matches, and recover match state if a device loses connection or is restarted.
          </li>
        </ul>

        <h2 style={styles.h2}>How We Use Your Information</h2>
        <p style={styles.p}>For tournament notification features, your information is used solely to:</p>
        <ul style={styles.ul}>
          <li style={styles.li}>Match your phone number to your player profile in the tournament system</li>
          <li style={styles.li}>Send you push notifications when your match is assigned to a table</li>
          <li style={styles.li}>Allow tournament organisers to notify you about your matches</li>
        </ul>
        
        <p style={styles.p}>
          For the manual shot clock and scoreboard features, no data is collected or transmitted. 
          All match information remains on your device.
        </p>

        <div style={styles.highlight}>
          <p style={{...styles.p, marginBottom: '0.5rem'}}><strong>We do not:</strong></p>
          <ul style={{...styles.ul, marginBottom: 0}}>
            <li style={styles.li}>Sell your personal information to third parties</li>
            <li style={styles.li}>Use your data for advertising or marketing purposes</li>
            <li style={styles.li}>Share your information with anyone outside the tournament system</li>
          </ul>
        </div>

        <h2 style={styles.h2}>Data Storage</h2>
        <p style={styles.p}>
          Your data is stored securely using Google Firebase, a cloud platform that provides 
          industry-standard security measures. Data is stored in the European Union (Firebase 
          Realtime Database in europe-west1).
        </p>

        <h2 style={styles.h2}>Data Retention</h2>
        <p style={styles.p}>
          Your phone number and device token are retained for as long as you have the app 
          registered on your device. Device tokens that have not been used for an extended 
          period may be automatically removed to maintain system efficiency.
        </p>

        <h2 style={styles.h2}>Your Rights</h2>
        <p style={styles.p}>You have the right to:</p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            <strong>Delete your data</strong> — To remove your push notification token, use the "Enter a New Mobile Number" option in the app.
            To request deletion of your full player registration or any other personal data we hold, please contact us at{' '}
            <a href="mailto:support@cueclockpro.com" style={styles.link}>support@cueclockpro.com</a>{' '}
            or use the data deletion request option within the app.
          </li>
          <li style={styles.li}>
            <strong>Access your data</strong> — Contact us to request information about what data we hold
          </li>
          <li style={styles.li}>
            <strong>Withdraw consent</strong> — Uninstall the app or unregister your device at any time
          </li>
        </ul>

        <h2 style={styles.h2}>Third-Party Services</h2>
        <p style={styles.p}>The app uses the following third-party services:</p>
        <ul style={styles.ul}>
          <li style={styles.li}><strong>Google Firebase</strong> — For data storage and cloud functions</li>
          <li style={styles.li}><strong>Expo</strong> — For push notification delivery</li>
        </ul>
        <p style={styles.p}>These services have their own privacy policies governing their use of data.</p>

        <h2 style={styles.h2}>Children's Privacy</h2>
        <p style={styles.p}>
          The app is not directed at children under 13. We do not knowingly collect personal 
          information from children under 13. If you believe we have collected information 
          from a child under 13, please contact us.
        </p>

        <h2 style={styles.h2}>Changes to This Policy</h2>
        <p style={styles.p}>
          We may update this privacy policy from time to time. Any changes will be posted 
          on this page with an updated revision date.
        </p>

        <h2 style={styles.h2}>Contact Us</h2>
        <p style={styles.p}>
          If you have any questions about this privacy policy or your data, please contact us at:
        </p>
        <p style={styles.p}>
          <a href="mailto:support@cueclockpro.com" style={styles.link}>support@cueclockpro.com</a>
        </p>
      </div>
    </div>
  );
}

export default PrivacyPolicy;